import { useState, type ReactElement } from 'react';
import { Plus } from 'lucide-react';
import { Button, Typography } from 'antd';

import type { DealStage } from '../api/deals.client.js';
import type { KanbanColumnModel } from '../lib/kanban.js';
import { DealCard } from './DealCard.js';

const { Text } = Typography;

export interface KanbanBoardProps {
  columns: KanbanColumnModel[];
  isUpdating: boolean;
  onCreateInStage: (stage: DealStage) => void;
  onMoveDeal: (dealId: string, toStage: DealStage) => void;
  onToggleSelected: (dealId: string) => void;
  selectedIds: ReadonlySet<string>;
}

export function KanbanBoard({
  columns,
  isUpdating,
  onCreateInStage,
  onMoveDeal,
  onToggleSelected,
  selectedIds,
}: KanbanBoardProps): ReactElement {
  const [draggedDealId, setDraggedDealId] = useState<string | null>(null);

  return (
    <div className="flex h-full w-full overflow-x-auto pb-4" data-testid="deals-kanban-board" style={{ gap: 16 }}>
        {columns.map((column) => (
          <section
            key={column.stage}
            data-testid={`kanban-column-${column.stage}`}
            className="flex flex-col h-full min-w-0 rounded-xl"
            style={{ width: 280, minWidth: 260, backgroundColor: '#f9f7f4', border: '1px solid #e7e5e4' }}
            onDragOver={(event) => {
              event.preventDefault();
            }}
            onDrop={(event) => {
              event.preventDefault();
              if (draggedDealId !== null) {
                onMoveDeal(draggedDealId, column.stage);
              }
              setDraggedDealId(null);
            }}
          >
            <header className="flex items-center justify-between border-b" style={{ padding: '12px 16px', borderColor: '#e7e5e4', backgroundColor: '#fffdfa', borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
              <h2 className="flex items-center gap-2 text-sm font-semibold text-[#44403c]">
                <span className="size-1.5 rounded-full bg-amber-600" />
                {column.title}
              </h2>
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center rounded-full" style={{ backgroundColor: '#f5f5f4', minWidth: 22, height: 22, padding: '0 6px', fontSize: 10, color: '#a8a29e', fontWeight: 600 }}>
                  {column.count}
                </div>
                <Button
                  type="text"
                  size="small"
                  aria-label={`Создать сделку в ${column.stage}`}
                  icon={<Plus size={14} />}
                  onClick={() => onCreateInStage(column.stage)}
                  style={{ width: 24, height: 24, padding: 0, color: '#78716c' }}
                />
              </div>
            </header>
            <div className="flex-1 overflow-y-auto hide-scrollbar flex flex-col" style={{ padding: 12, gap: 10 }}>
              {column.cards.length > 0 ? (
                column.cards.map((card) => (
                  <DealCard
                    key={card.dealId}
                    card={card}
                    isSelected={selectedIds.has(card.dealId)}
                    isUpdating={isUpdating}
                    onMoveForward={onMoveDeal}
                    onToggleSelected={onToggleSelected}
                    onDragStart={setDraggedDealId}
                  />
                ))
              ) : (
                <div className="rounded-lg border border-dashed flex items-center justify-center" style={{ borderColor: '#d6d1cb', padding: '32px 20px', backgroundColor: 'transparent' }}>
                  <Text type="secondary" style={{ fontSize: 12, textAlign: 'center' }}>В этой стадии пока нет сделок.</Text>
                </div>
              )}
            </div>
          </section>
        ))}
    </div>
  );
}
